import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { catchError, defer, finalize, map, Observable, of, shareReplay } from 'rxjs';
import { LoadingState } from '../../../../shared/types/loading.type';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { outputError } from '../../../../shared/utils/output-error.function';
import { DashboardClient } from '../../../../core/openapi/bff-admin-api/api/dashboard.client';
import { DashboardMetricsResponse } from '../../../../core/openapi/bff-admin-api/model/dashboard-metrics-response';
import { OverviewDashboardSummary } from './overview-dashboard.model';

@Injectable()
export class OverviewDashboardService {
  private readonly dashboardClient = inject(DashboardClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly _loadingStatus: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  public readonly loadingStatus: Signal<LoadingState> = this._loadingStatus.asReadonly();

  public readonly summary$: Observable<OverviewDashboardSummary> = defer(() => {
    this._loadingStatus.set(LoadingState.LOADING);
    return this.dashboardClient.v1DashboardMetricsGet();
  }).pipe(
    map(
      (response: DashboardMetricsResponse): OverviewDashboardSummary => ({
        ...response,
        totalDeviceCount: response.totalEquipmentCount,
        activeDeviceCount: response.activeEquipmentCount,
        freeDeviceCount: response.totalEquipmentCount - response.activeEquipmentCount,
      }),
    ),
    catchError((error) => {
      this.toastService.error({
        message: this.translateService.instant(
          'monitoring.dashboard.overview-dashboard.toast.overview-dashboard-featching-failed',
          { error: outputError(error, this.translateService.instant('errors.unknown')) },
        ),
      });
      return of({} as OverviewDashboardSummary);
    }),
    finalize(() => this._loadingStatus.set(LoadingState.READY)),
    shareReplay(1),
  );
}
